import classNames from "classnames";
import compact from "lodash/compact";
import React, { FunctionComponent, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { v1 as uuid } from "uuid";
import { fontTagToReadableText } from "../../../../../../agreed/fonts";
import { ReactComponent as IconColor } from "../../../../../../icons/16x16/color-picker.svg";
import { ReactComponent as IconLayout } from "../../../../../../icons/16x16/layout.svg";
import { ReactComponent as IconType } from "../../../../../../icons/16x16/type.svg";
import { CustomizationAction } from "../../../../../../states/customization";
import { Configuration } from "../../../../../../types/customization/configuration";
import { Content } from "../../../../../../types/customization/content";
import { Template, TemplateColorDefinition } from "../../../../../../types/customization/template";
import {
  findPageAndDefinition,
  findPageDefinitionsWithValidCondition,
} from "../../../../../../utils/customization/finders";
import { buildRotationPagesMapping } from "../../../../../../utils/customization/page";
import { showCellColorOverPageColor } from "../../../../../../utils/customization/template";
import ConfirmModal from "../../../../../molecules/ConfirmModal";
import PageLayouts from "../../../../../molecules/customization/PageLayouts";
import OptionList from "../../../../../molecules/customization/panels/OptionList";
import TranslationByType from "../../../../../molecules/customization/TranslationByType";
import "./style.scss";

// disabled (https://cheerz0.atlassian.net/browse/EVOL-1232?focusedCommentId=22662) :
// const MAX_ITEMS_BY_LINE = 4;

interface Props {
  configuration: Configuration | undefined;
  content: Content;
  dispatch: React.Dispatch<CustomizationAction>;
  template: Template;
  defaultFontKey: string | undefined;
  pageKey: string;
}
const StylesContent: FunctionComponent<Props> = ({
  configuration,
  content,
  dispatch,
  template,
  defaultFontKey,
  pageKey,
}) => {
  const intl = useIntl();

  const undoKey = useMemo(() => `StylesContent-${uuid()}`, []);

  const { page, pageDefinition } = findPageAndDefinition(pageKey, template, content);
  const pageDefinitions = findPageDefinitionsWithValidCondition(template, content, {
    type: pageDefinition.type,
    filterType: pageDefinition.filterType,
  });
  const rotationPairsMapping = buildRotationPagesMapping(template.pageDefinitions);
  const hasLayouts =
    pageDefinitions.length > 2 ||
    (pageDefinitions.length === 2 && Object.keys(rotationPairsMapping).length === 0);

  const hasMultiplePages = content.pages.length > 1;
  const hasForcedPackColors = template.contentPageConfiguration.forcePackColor;

  const [hideLayouts, setHideLayouts] = useState<boolean>(false);
  // disabled (https://cheerz0.atlassian.net/browse/EVOL-1232?focusedCommentId=22662) :
  // const [hideLayouts, setHideLayouts] = useState<boolean>(
  //   hasMobileLayout === false && pageDefinitions.length > MAX_ITEMS_BY_LINE
  // );

  const [showApplyAllModal, setShowApplyAllModal] = useState<boolean>(false);
  const [applyAllModalType, setApplyAllModalType] = useState<string>("");
  const handleDismissApplyAllModal = () => {
    setShowApplyAllModal(false);
  };
  const handleConfirmApplyAllModal = (type?: string) => {
    setShowApplyAllModal(false);
    const typeToUse = type || applyAllModalType;

    if (typeToUse === "color") {
      dispatch({
        colorDefinitionKey: page.backgroundColorKey,
        type: "changeAllPagesColorDefinition",
        undoKey,
      });
    } else if (typeToUse === "layout") {
      dispatch({
        options: configuration?.options,
        pageDefinitionKey: page.pageDefinitionKey,
        type: "changeAllPagesPageDefinition",
        undoKey,
      });
    } else if (typeToUse === "text") {
      const fontKey = page.editableTexts[0]?.fontKey || defaultFontKey;
      if (fontKey) {
        dispatch({
          fontDefinitionKey: fontKey,
          type: "changeAllPagesFontDefinition",
          undoKey,
        });
      }
    }
  };

  const handleApplyAllBehaviour = (type: string) => {
    const onceDisplayed = localStorage.getItem("cheerz_applyAll_modale");
    if (onceDisplayed) {
      handleConfirmApplyAllModal(type);
    } else {
      localStorage.setItem("cheerz_applyAll_modale", "1");
      setApplyAllModalType(type);
      setShowApplyAllModal(true);
    }
  };
  const handleClickApplyAllColors = () => {
    handleApplyAllBehaviour("color");
  };
  const handleClickApplyAllLayouts = () => {
    handleApplyAllBehaviour("layout");
  };
  const handleClickApplyAllTexts = () => {
    handleApplyAllBehaviour("text");
  };

  const colorItems = useMemo(() => {
    const backgroundKeys = pageDefinition.backgroundColorKeys;
    const pageColorDefinitions = template.colorDefinitions.filter((colorDefinition) =>
      backgroundKeys.includes(colorDefinition.key)
    );
    if (pageColorDefinitions.length < 2) {
      return null;
    }

    let selectedColors: TemplateColorDefinition | undefined;

    const showCellColor = showCellColorOverPageColor(template.templateType, pageColorDefinitions);

    const colorValuesFormatted = pageColorDefinitions.map((colorDefinition) => {
      if (colorDefinition.key === page.backgroundColorKey) {
        selectedColors = colorDefinition;
      }

      const backgroundColor =
        showCellColor && colorDefinition.cellColors
          ? colorDefinition.cellColors[0].active.backgroundValue
          : colorDefinition.backgroundValue;

      return {
        key: colorDefinition.key,
        onSelect: () => {
          dispatch({ type: "setPageToFocus", pageKey });

          if (hasForcedPackColors) {
            dispatch({
              colorDefinitionKey: colorDefinition.key,
              type: "changeAllPagesColorDefinition",
              undoKey,
            });
          } else {
            dispatch({
              colorDefinitionKey: colorDefinition.key,
              pageKey,
              type: "changePageColorDefinition",
              undoKey,
            });
          }
        },
        value: backgroundColor,
      };
    });

    const applyAllButton =
      hasMultiplePages && hasForcedPackColors === false
        ? {
            label: intl.formatMessage({ id: "kustom_PageEditionPanel_options_applyAll" }),
            onClick: handleClickApplyAllColors,
          }
        : undefined;

    return (
      <OptionList
        globalButton={applyAllButton}
        icon={<IconColor />}
        optionItems={compact(colorValuesFormatted)}
        selectedValue={selectedColors && selectedColors.key}
        title={intl.formatMessage({ id: "kustom_OptionsPanel_color" })}
        type="color"
      />
    );
  }, [
    dispatch,
    hasMultiplePages,
    intl,
    page.backgroundColorKey,
    page.editableTexts,
    pageDefinition.backgroundColorKeys,
    pageKey,
    template.colorDefinitions,
    undoKey,
  ]);

  const textItems = useMemo(() => {
    const editableTextDefinitions = pageDefinition.elementDefinitions.filter(
      (elementDefinition) => elementDefinition.type === "editable-text"
    );
    const editableTexts = page.editableTexts;

    if (editableTexts.length > 0 || editableTextDefinitions.length > 0) {
      const fontDefinitionsFormatted = template.fontDefinitions.map((fontDefinition) => {
        return {
          key: fontDefinition.key,
          label: fontTagToReadableText(fontDefinition.fontTag),
          onSelect: () => {
            dispatch({ type: "setPageToFocus", pageKey });
            dispatch({
              fontDefinitionKey: fontDefinition.key,
              pageKey: page.key,
              type: "changePageFontDefinition",
              undoKey,
            });
          },
          value: fontDefinition.fontTag,
        };
      });

      if (fontDefinitionsFormatted.length > 1) {
        const selectedFonts = page.editableTexts.reduce((fonts: string[], editableText) => {
          if (fonts.includes(editableText.fontKey) === false) {
            fonts.push(editableText.fontKey);
          }
          return fonts;
        }, []);
        const selectedFont =
          selectedFonts.length === 1
            ? selectedFonts[0]
            : defaultFontKey
            ? defaultFontKey
            : undefined;

        const applyAllButton = hasMultiplePages
          ? {
              label: intl.formatMessage({ id: "kustom_PageEditionPanel_options_applyAll" }),
              onClick: handleClickApplyAllTexts,
            }
          : undefined;

        return (
          <OptionList
            globalButton={applyAllButton}
            icon={<IconType />}
            optionItems={fontDefinitionsFormatted}
            selectedValue={selectedFont}
            title={intl.formatMessage({ id: "kustom_OptionsPanel_type" })}
            type="text"
          />
        );
      }
    }
  }, [
    defaultFontKey,
    dispatch,
    hasMultiplePages,
    intl,
    page.editableTexts,
    page.key,
    pageDefinition.elementDefinitions,
    template.fontDefinitions,
    undoKey,
  ]);

  const handleClickSeeMore = () => {
    setHideLayouts(false);
  };

  const optionsApplyAllButton = hasMultiplePages
    ? {
        label: intl.formatMessage({ id: "kustom_PageEditionPanel_options_applyAll" }),
        onClick: handleClickApplyAllLayouts,
      }
    : undefined;

  return (
    <div className={classNames("StylesContent", { "StylesContent--hiddenLayouts": hideLayouts })}>
      {hasLayouts && (
        <OptionList
          globalButton={optionsApplyAllButton}
          icon={<IconLayout />}
          optionItems={[]}
          title={intl.formatMessage({ id: "kustom_PageEditionPanel_layout" })}
          type="layout"
        >
          <>
            <PageLayouts
              currentPageDefinitionKey={page.pageDefinitionKey}
              pageKey={pageKey}
              undoKey={undoKey}
            />
            {hideLayouts && (
              <div className="StylesContent-content-link" onClick={handleClickSeeMore}>
                {intl.formatMessage({ id: "kustom_PageEditionPanel_content_seeMore" })} +
              </div>
            )}
          </>
        </OptionList>
      )}

      {textItems}

      {colorItems}

      <ConfirmModal
        confirmLabel={intl.formatMessage({ id: "kustom_OptionsPanel_apply" })}
        display={showApplyAllModal}
        onConfirm={() => handleConfirmApplyAllModal()}
        onDismiss={handleDismissApplyAllModal}
      >
        <div className="StylesContent-applyAll-modale">
          <h2 className="StylesContent-applyAll-modale-title">
            {intl.formatMessage({ id: "kustom_PageEditionPanel_options_applyAll" })}
          </h2>
          <div className="StylesContent-applyAll-modale-content">
            <TranslationByType
              pageName={configuration?.pageName}
              pageNameChoice="plural"
              templateType={template.templateType}
              translationBase="kustom_PageEditionPanel_options_applyAll_alert"
            />
          </div>
        </div>
      </ConfirmModal>
    </div>
  );
};

export default StylesContent;
